import { Badge, type BadgeProps } from '@chakra-ui/react';
import type React from 'react';

export type StatusType =
  | 'active'
  | 'due-soon'
  | 'past-due'
  | 'archived'
  | 'blue'
  | 'purple'
  | 'default';

interface StatusBadgeProps extends Omit<BadgeProps, 'children'> {
  status?: StatusType;
  count?: number;
  label?: string;
  size?: 'sm' | 'md';
  bg?: string;
  color?: string;
}

// Helper function to get the right colors based on status
const getStatusColors = (status: StatusType) => {
  switch (status) {
    case 'active':
      return {
        bg: 'neutral.status.greenBg',
        color: 'neutral.status.green',
      };
    case 'due-soon':
      return {
        bg: 'neutral.status.orangeBg',
        color: 'neutral.status.orange',
      };
    case 'past-due':
      return {
        bg: 'neutral.status.redBg',
        color: 'neutral.status.red',
      };
    case 'archived':
      return {
        bg: 'gray.100',
        color: 'gray.500',
      };
    case 'blue':
      return {
        bg: 'blue.100',
        color: 'blue.500',
      };
    case 'purple':
      return {
        bg: 'purple.100',
        color: 'purple.500',
      };
    default:
      return {
        bg: 'gray.100',
        color: 'gray.500',
      };
  }
};

export const StatusBadge: React.FC<StatusBadgeProps> = ({
  status = 'default',
  count,
  label,
  size = 'md',
  bg,
  color,
  ...props
}) => {
  const colors = getStatusColors(status);

  return (
    <Badge
      bg={bg || colors.bg}
      color={color || colors.color}
      borderRadius="full"
      px={size === 'sm' ? 2 : 2}
      py={size === 'sm' ? 0 : 1}
      fontSize={'xs'}
      fontWeight="medium"
      {...props}
    >
      {label || (count !== undefined ? count : '')}
    </Badge>
  );
};
